import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";

const PLANS = [
  ["starter", 49],
  ["pro", 99],
  ["business", 199],
];
const ACTIVE = ["active", "trialing", "past_due"];

export default function Billing() {
  const { t, locale } = useLang();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [yearly, setYearly] = useState(false);
  const load = () => api.get("/billing/me").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const sub = data.subscription;
  const usage = data.usage;
  const live = sub && ACTIVE.includes(sub.status) && sub.source !== "trial";
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(locale) : "");
  const lk = (plan) => `${plan}_${yearly ? "yearly" : "monthly"}`;

  const act = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const subscribe = (lookup_key) => act(lookup_key, async () => {
    const { data: d } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
    toast.info(t("redirecting"));
    window.location.href = d.checkout_url;
  });
  const change = (lookup_key) => act(lookup_key, async () => { await api.post("/billing/change", { lookup_key, origin_url: window.location.origin }); await load(); toast.success(t("saved")); });
  const cancel = () => act("cancel", async () => { await api.post("/billing/cancel"); await load(); });
  const resume = () => act("resume", async () => { await api.post("/billing/resume"); await load(); });

  return (
    <Panel className="mt-6 max-w-3xl p-6" data-testid="billing-panel">
      <h3 className="font-display text-lg font-bold">{t("billing")}</h3>
      <p className="mt-1 text-sm text-slate-500">{t("billingSubtitle")}</p>

      <div data-testid="billing-current" className={`mt-5 rounded-xl border p-4 ${live ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
        {sub?.source === "trial" ? (
          <div data-testid="billing-trial" className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">{sub.expired ? t("trialExpired") : t("trialActive")}</p>
              <p className="font-display text-2xl font-extrabold text-slate-900">{t("plan_pro")} · {t("freeTrial")}</p>
              <p className="mt-1 text-sm text-slate-600">{sub.expired ? t("trialExpiredText") : `${t("trialEndsOn")} ${fmtDate(sub.trial_end)}`}</p>
            </div>
          </div>
        ) : live ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">{t("currentPlan")}</p>
              <p className="font-display text-2xl font-extrabold text-slate-900" data-testid="billing-plan-name">{t(`plan_${sub.plan}`)} <span className="text-sm font-semibold text-slate-500">· {sub.lookup_key?.endsWith("yearly") ? t("yearly") : t("monthly")}</span></p>
              <p className="mt-1 text-sm text-slate-600">
                <span className="me-2 rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800" data-testid="billing-status">{t(`sub_${sub.status}`)}</span>
                {sub.cancel_at_period_end ? t("endsOn") : t("renewsOn")} {fmtDate(sub.current_period_end)}
              </p>
            </div>
            {sub.cancel_at_period_end ? (
              <Button data-testid="billing-resume-btn" variant="outline" disabled={!!busy} onClick={resume}>{busy === "resume" && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t("resumeSub")}</Button>
            ) : (
              <Button data-testid="billing-cancel-btn" variant="outline" disabled={!!busy} onClick={cancel} className="text-[#A50E1F] hover:text-[#A50E1F]">{busy === "cancel" && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t("cancelSub")}</Button>
            )}
          </div>
        ) : (
          <div>
            <p className="font-semibold text-slate-800">{t("noPlan")}</p>
            <p className="mt-1 text-sm text-slate-500">{t("noPlanText")}</p>
          </div>
        )}
      </div>

      {!usage.exempt && (
        <div data-testid="billing-usage" className="mt-4 flex flex-wrap gap-6 rounded-xl border border-slate-200 p-4 text-sm">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{t("usageInvoices")}</p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums" data-testid="billing-usage-invoices">{usage.invoices_used} / {usage.invoices_limit ?? t("unlimited")}</p>
            {usage.invoices_limit && <div className="mt-1 h-1.5 w-40 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-[#007A3D]" style={{ width: `${Math.min(100, (usage.invoices_used / usage.invoices_limit) * 100)}%` }} /></div>}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">AI</p>
            <p className={`mt-1 font-semibold ${usage.ai ? "text-[#007A3D]" : "text-slate-500"}`} data-testid="billing-usage-ai">{t(usage.ai ? "aiIncluded" : "aiNotIncluded")}</p>
          </div>
        </div>
      )}

      <div className="mt-5 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-1 text-xs font-bold">
        <button type="button" data-testid="billing-monthly" onClick={() => setYearly(false)} className={`rounded-full px-3 py-1.5 ${!yearly ? "bg-[#007A3D] text-white" : "text-slate-500"}`}>{t("monthly")}</button>
        <button type="button" data-testid="billing-yearly" onClick={() => setYearly(true)} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 ${yearly ? "bg-[#007A3D] text-white" : "text-slate-500"}`}>{t("yearly")}<span className="rounded-full bg-[#D4AF37] px-1.5 py-0.5 text-[9px] uppercase text-[#1A1400]">{t("twoMonthsFree")}</span></button>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {PLANS.map(([plan, price]) => {
          const key = lk(plan);
          const isCurrent = live && sub.lookup_key === key;
          return (
            <div key={plan} data-testid={`billing-plan-${plan}`} className={`flex flex-col rounded-xl border p-4 ${isCurrent ? "border-[#007A3D] ring-1 ring-[#007A3D]" : "border-slate-200"}`}>
              <p className="font-display text-base font-bold">{t(`plan_${plan}`)}</p>
              <p className="mt-2 font-mono text-2xl font-bold tabular-nums">{yearly ? price * 10 : price} AED <span className="text-xs font-normal text-slate-400">{yearly ? t("perYear") : t("perMonth")}</span></p>
              <div className="mt-4 flex-1" />
              {isCurrent ? (
                <div className="flex items-center gap-1.5 text-sm font-semibold text-[#007A3D]"><CheckCircle2 className="h-4 w-4" />{t("currentPlan")}</div>
              ) : (
                <Button data-testid={`billing-choose-${plan}`} size="sm" disabled={!!busy} onClick={() => (live ? change(key) : subscribe(key))} className="bg-[#007A3D] hover:bg-[#005E2E]">
                  {busy === key && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{live ? t("switchPlan") : t("choosePlan")}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {data.invoices.length > 0 && (
        <div className="mt-6">
          <h4 className="mb-2 text-sm font-bold text-slate-700">{t("invoicesHistory")}</h4>
          <table className="data-table compact" data-testid="billing-invoices">
            <thead><tr><th>{t("date")}</th><th>{t("code")}</th><th>{t("status")}</th><th className="text-end">{t("amount")}</th><th /></tr></thead>
            <tbody>
              {data.invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{fmtDate(inv.date)}</td><td className="font-mono text-xs">{inv.number}</td><td>{inv.status}</td>
                  <td className="text-end font-mono tabular-nums">{inv.amount.toFixed(2)} {inv.currency.toUpperCase()}</td>
                  <td className="text-end">{inv.url && <a href={inv.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#007A3D]">{t("viewInvoice")}<ExternalLink className="h-3 w-3" /></a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
